import React, { useEffect, useMemo, useState } from 'react';
import { Copy, Edit3, Loader2, Nfc, Power, ShieldAlert, ShieldCheck, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, supabase } from '@/lib/supabaseClient';
import { runNfcReadOnlyPreflight } from '@/lib/nfcReader';
import { provisionNativeNfcTag, verifyNativeReadOnlyEV2 } from '@/lib/nfcNativeChannel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import SettingsLayout from '@/components/settings/SettingsLayout';
import SettingsCard from '@/components/settings/SettingsCard';

const EMPTY_FORM = {
  company_id: '',
  location_id: '',
  department_id: '',
  code: '',
  name: '',
  point_type: 'GENERAL',
  description: '',
};

const POINT_TYPES = [
  { value: 'ENTRANCE', label: 'Entrata' },
  { value: 'EXIT', label: 'Uscita' },
  { value: 'GENERAL', label: 'Generale' },
];

const STATUS_LABELS = {
  ACTIVE: 'Attivo',
  INACTIVE: 'Inattivo',
  REVOKED: 'Revocato',
  CLOSED: 'Chiuso',
};

function statusClass(status) {
  return {
    ACTIVE: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    INACTIVE: 'border-amber-200 bg-amber-50 text-amber-700',
    REVOKED: 'border-red-200 bg-red-50 text-red-700',
    CLOSED: 'border-stone-200 bg-stone-100 text-stone-600',
  }[status] || 'border-stone-200 bg-stone-50 text-stone-600';
}

function tagLabel(point) {
  if (point.tag_status === 'ASSOCIATED') return 'Associato · verificato';
  if (point.tag_status === 'REVOKED') return 'Revocato';
  return 'Non associato';
}

function tagClass(point) {
  if (point.tag_status === 'ASSOCIATED') return 'border-blue-200 bg-blue-50 text-blue-700';
  if (point.tag_status === 'REVOKED') return 'border-red-200 bg-red-50 text-red-700';
  return 'border-stone-200 bg-stone-50 text-stone-500';
}

function formatNativeDiagnostic(diagnostic) {
  const lines = [
    'schema=2',
    `phase=${diagnostic.phase}`,
    `code=${diagnostic.code}`,
  ];
  if (diagnostic.coreNfcCode && diagnostic.coreNfcCategory) {
    lines.push(
      `core_nfc_code=${diagnostic.coreNfcCode}`,
      `core_nfc_category=${diagnostic.coreNfcCategory}`,
    );
  }
  if (diagnostic.coreNfcDomain && diagnostic.coreNfcNumericCode !== undefined) {
    lines.push(
      `core_nfc_domain=${diagnostic.coreNfcDomain}`,
      `core_nfc_numeric_code=${diagnostic.coreNfcNumericCode}`,
    );
  }
  lines.push(`session_elapsed_ms=${diagnostic.sessionElapsedMs}`);
  if (diagnostic.exchangeElapsedMs !== undefined) {
    lines.push(`exchange_elapsed_ms=${diagnostic.exchangeElapsedMs}`);
  }
  if (diagnostic.responseLength !== undefined) {
    lines.push(
      `response_length=${diagnostic.responseLength}`,
      `sw1=${diagnostic.sw1}`,
      `sw2=${diagnostic.sw2}`,
    );
  }
  lines.push(
    `session_id=${diagnostic.sessionId}`,
    `correlation_id=${diagnostic.correlationId}`,
  );
  return lines.join('\n');
}

async function copyDiagnosticText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const input = document.createElement('textarea');
  input.value = text;
  input.setAttribute('readonly', '');
  input.style.position = 'fixed';
  input.style.opacity = '0';
  document.body.appendChild(input);
  input.select();
  const copied = document.execCommand('copy');
  input.remove();
  if (!copied) throw new Error('Copia non disponibile');
}

function PointActions({
  point, onEdit, onPreflight, onEv2ReadOnly, onProvision, onActivate, onDeactivate, onRevoke,
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {point.status !== 'REVOKED' && point.status !== 'CLOSED' && (
        <Button variant="outline" size="sm" onClick={() => onEdit(point)} data-testid={`nfc-edit-${point.id}`}>
          <Edit3 className="mr-1.5 h-3.5 w-3.5" /> Modifica
        </Button>
      )}
      {point.status !== 'REVOKED' && point.status !== 'CLOSED' && (
        <Button variant="outline" size="sm" onClick={() => onPreflight(point)} data-testid={`nfc-verify-${point.id}`}>
          <Nfc className="mr-1.5 h-3.5 w-3.5" /> Verifica tag (sola lettura)
        </Button>
      )}
      {point.status === 'ACTIVE' && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onEv2ReadOnly(point)}
          data-testid={`nfc-ev2-readonly-${point.id}`}
        >
          <ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Diagnostica EV2 autenticata
        </Button>
      )}
      {point.name === 'NFC San Pietro' && point.status === 'ACTIVE'
        && point.tag_status !== 'ASSOCIATED' && point.tag_status !== 'REVOKED' && (
        <Button
          variant="destructive"
          size="sm"
          onClick={() => onProvision(point)}
          data-testid={`nfc-provision-${point.id}`}
        >
          <Nfc className="mr-1.5 h-3.5 w-3.5" /> Associa tag su iPhone
        </Button>
      )}
      {point.status === 'INACTIVE' && point.tag_status === 'ASSOCIATED' && (
        <Button size="sm" onClick={() => onActivate(point)} data-testid={`nfc-activate-${point.id}`}>
          <Power className="mr-1.5 h-3.5 w-3.5" /> Attiva
        </Button>
      )}
      {point.status === 'ACTIVE' && (
        <Button variant="outline" size="sm" onClick={() => onDeactivate(point)} data-testid={`nfc-deactivate-${point.id}`}>
          <Power className="mr-1.5 h-3.5 w-3.5" /> Disattiva
        </Button>
      )}
      {point.status !== 'REVOKED' && point.status !== 'CLOSED' && (
        <Button
          variant="outline"
          size="sm"
          className="text-red-600 hover:border-red-200 hover:bg-red-50 hover:text-red-700"
          onClick={() => onRevoke(point)}
          data-testid={`nfc-revoke-${point.id}`}
        >
          <XCircle className="mr-1.5 h-3.5 w-3.5" /> Revoca
        </Button>
      )}
    </div>
  );
}

export default function NfcPoints() {
  const [points, setPoints] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState(null);
  const [preflightPoint, setPreflightPoint] = useState(null);
  const [preflightResult, setPreflightResult] = useState(null);
  const [preflightError, setPreflightError] = useState('');
  const [preflightBusy, setPreflightBusy] = useState(false);
  const [nativeDiagnostic, setNativeDiagnostic] = useState(null);

  const loadHierarchy = async () => {
    const [companyRows, locationRows] = await Promise.all([
      apiFetch('/api/companies'),
      apiFetch('/api/locations'),
    ]);
    const departmentRows = (await Promise.all(
      locationRows.map(async (location) => {
        const rows = await apiFetch(`/api/departments?location_id=${location.id}`);
        return rows.map((row) => ({ ...row, location_id: location.id }));
      }),
    )).flat();
    setCompanies(companyRows);
    setLocations(locationRows);
    setDepartments(departmentRows);
  };

  const loadPoints = async () => {
    const rows = await apiFetch('/api/nfc/points');
    setPoints(rows);
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([loadHierarchy(), loadPoints()]);
    } catch (loadError) {
      setError(loadError.message || 'Impossibile caricare i punti NFC');
    } finally {
      setLoading(false);
    }
  };

  // The loader is intentionally stable for this page lifecycle; API calls are
  // re-triggered explicitly after each mutation below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const availableLocations = useMemo(
    () => locations.filter((location) => !editor?.company_id || location.company_id === editor.company_id),
    [locations, editor?.company_id],
  );
  const availableDepartments = useMemo(
    () => departments.filter((department) => department.location_id === editor?.location_id),
    [departments, editor?.location_id],
  );

  const openCreate = () => {
    const companyId = companies[0]?.id || '';
    const locationId = locations.find((location) => location.company_id === companyId)?.id || '';
    setEditor({ mode: 'create', form: { ...EMPTY_FORM, company_id: companyId, location_id: locationId } });
  };

  const openEdit = (point) => {
    setEditor({
      mode: 'edit',
      point,
      form: {
        company_id: point.company_id,
        location_id: point.location_id,
        department_id: point.department_id || '',
        code: point.code,
        name: point.name,
        point_type: point.point_type,
        description: point.description || '',
      },
    });
  };

  const updateEditor = (key, value) => {
    setEditor((current) => {
      const form = { ...current.form, [key]: value };
      if (key === 'company_id') {
        form.location_id = locations.find((location) => location.company_id === value)?.id || '';
        form.department_id = '';
      }
      if (key === 'location_id') form.department_id = '';
      return { ...current, form };
    });
  };

  const savePoint = async () => {
    if (!editor || saving) return;
    setSaving(true);
    try {
      const { form } = editor;
      const body = {
        ...form,
        department_id: form.department_id || null,
        description: form.description.trim() || null,
      };
      await apiFetch(
        editor.mode === 'create' ? '/api/nfc/points' : `/api/nfc/points/${editor.point.id}`,
        { method: editor.mode === 'create' ? 'POST' : 'PATCH', body: JSON.stringify(body) },
      );
      toast.success(editor.mode === 'create' ? 'Punto NFC creato' : 'Punto NFC aggiornato');
      setEditor(null);
      await loadPoints();
    } catch (saveError) {
      toast.error(saveError.message || 'Impossibile salvare il punto NFC');
    } finally {
      setSaving(false);
    }
  };


  const runAction = async (point, action, successMessage) => {
    if (saving) return;
    setSaving(true);
    try {
      await apiFetch(`/api/nfc/points/${point.id}/${action}`, { method: 'POST' });
      toast.success(successMessage);
      await loadPoints();
    } catch (actionError) {
      toast.error(actionError.message || 'Operazione non riuscita');
    } finally {
      setSaving(false);
    }
  };

  const revoke = (point) => {
    if (window.confirm(`Revocare definitivamente il punto "${point.name}" e il relativo tag?`)) {
      runAction(point, 'revoke', 'Punto NFC revocato');
    }
  };

  const openPreflight = (point) => {
    setPreflightPoint(point);
    setPreflightResult(null);
    setPreflightError('');
  };

  const runPreflight = async () => {
    if (preflightBusy) return;
    setPreflightBusy(true);
    setPreflightResult(null);
    setPreflightError('');
    try {
      setPreflightResult(await runNfcReadOnlyPreflight());
    } catch (preflightFailure) {
      setPreflightError(preflightFailure.message || 'Verifica NFC non riuscita');
    } finally {
      setPreflightBusy(false);
    }
  };

  const provisionTag = async (point) => {
    if (saving) return;
    setSaving(true);
    try {
      const { data: { session } = {} } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Sessione scaduta. Accedi nuovamente.');
      const context = await apiFetch('/api/ceo/tenant-context');
      if (!context?.active_tenant_id) throw new Error('Seleziona il tenant del punto NFC.');
      await provisionNativeNfcTag({
        accessToken: session.access_token,
        tenantContext: context.active_tenant_id,
        pointId: point.id,
      });
      toast.success('Tag associato e verificato per NFC San Pietro');
      await loadPoints();
    } catch (failure) {
      if (failure?.code === 'NFC_READ_INTERRUPTED' && failure.diagnostic) {
        setNativeDiagnostic(failure.diagnostic);
      } else if (failure?.code === 'RECOVERY_REQUIRED') {
        toast.error('Provisioning interrotto; recupero richiesto. Non ripetere la scansione e contatta l’amministratore.');
      } else {
        toast.error('Associazione non confermata. Non ripetere la scansione e contatta l’amministratore.');
      }
    } finally {
      setSaving(false);
    }
  };

  const runAuthenticatedEv2ReadOnly = async (point) => {
    if (saving) return;
    setSaving(true);
    setNativeDiagnostic(null);
    try {
      const { data: { session } = {} } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Sessione scaduta. Accedi nuovamente.');
      const context = await apiFetch('/api/ceo/tenant-context');
      if (!context?.active_tenant_id) throw new Error('Seleziona il tenant del punto NFC.');
      await verifyNativeReadOnlyEV2({
        accessToken: session.access_token,
        tenantContext: context.active_tenant_id,
        pointId: point.id,
      });
      toast.success('Verifica EV2 autenticata completata in sola lettura');
    } catch (failure) {
      if (failure?.code === 'NFC_READ_INTERRUPTED' && failure.diagnostic) {
        setNativeDiagnostic(failure.diagnostic);
      } else {
        toast.error(failure?.code === 'RECOVERY_REQUIRED'
          ? 'Recupero richiesto. Non ripetere la scansione.'
          : 'Verifica EV2 non riuscita. Nessuna scrittura è stata richiesta.');
      }
    } finally {
      setSaving(false);
    }
  };

  const renderPoint = (point, compact = false) => (
    <div
      key={point.id}
      className={compact
        ? 'space-y-4 rounded-2xl border border-stone-200 bg-white p-4'
        : 'grid grid-cols-[minmax(180px,1.2fr)_minmax(130px,0.9fr)_minmax(100px,0.7fr)_minmax(120px,0.8fr)_minmax(150px,1fr)_auto] items-center gap-4 border-t border-stone-100 px-4 py-4 first:border-t-0'}
      data-testid={`nfc-point-${point.id}`}
    >
      <div className="min-w-0">
        <div className="font-semibold text-brand-navy">{point.name}</div>
        <div className="mt-1 text-xs font-mono text-stone-400">{point.code}</div>
        {compact && point.description && <p className="mt-2 text-sm text-stone-500">{point.description}</p>}
      </div>
      <div className={compact ? 'grid grid-cols-2 gap-3 text-sm' : ''}>
        <div className="text-sm text-stone-700">{point.company_name}</div>
        <div className="text-xs text-stone-500">{point.location_name}</div>
        {compact && <div className="text-xs text-stone-500">{point.department_name || 'Tutti i reparti'}</div>}
      </div>
      {!compact && <div className="text-sm text-stone-600">{point.department_name || '—'}</div>}
      <div className="text-sm text-stone-600">{POINT_TYPES.find((type) => type.value === point.point_type)?.label}</div>
      <div className="flex flex-wrap gap-2">
        <span className={`inline-flex rounded-full border px-2 py-1 text-[11px] font-semibold ${statusClass(point.status)}`}>
          {STATUS_LABELS[point.status]}
        </span>
        <span className={`inline-flex rounded-full border px-2 py-1 text-[11px] font-semibold ${tagClass(point)}`}>
          {point.tag_status === 'ASSOCIATED' ? <ShieldCheck className="mr-1 h-3.5 w-3.5" /> : <Nfc className="mr-1 h-3.5 w-3.5" />}
          {tagLabel(point)}
        </span>
      </div>
      <PointActions
        point={point}
        onEdit={openEdit}
        onPreflight={openPreflight}
        onEv2ReadOnly={runAuthenticatedEv2ReadOnly}
        onProvision={provisionTag}
        onActivate={(target) => runAction(target, 'activate', 'Punto NFC attivato')}
        onDeactivate={(target) => runAction(target, 'deactivate', 'Punto NFC disattivato')}
        onRevoke={revoke}
      />
    </div>
  );

  return (
    <SettingsLayout
      eyebrow="Impostazioni · Presenze"
      title="Punti NFC"
      description="Gestisci i punti fisici associati a azienda, locale e reparto. Questa sezione prepara il provisioning: non registra ancora entrate o uscite."
      breadcrumbs={[{ label: 'Impostazioni', to: '/impostazioni' }]}
      current="Punti NFC"
      testId="nfc-points-page"
    >
      <div className="space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-brand-navy">Catalogo punti fisici</p>
            <p className="text-sm text-stone-500">{points.length} {points.length === 1 ? 'punto configurato' : 'punti configurati'}</p>
          </div>
          <Button onClick={openCreate} data-testid="nfc-new-point">
            <Nfc className="mr-2 h-4 w-4" /> Nuovo punto NFC
          </Button>
        </div>

        {loading && (
          <SettingsCard className="flex items-center justify-center gap-2 p-10 text-sm text-stone-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Caricamento punti NFC…
          </SettingsCard>
        )}
        {!loading && error && (
          <SettingsCard className="flex items-start gap-3 border-red-200 bg-red-50 p-5 text-sm text-red-700">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" /> {error}
          </SettingsCard>
        )}
        {!loading && !error && !points.length && (
          <SettingsCard className="p-8 text-center">
            <Nfc className="mx-auto h-10 w-10 text-emerald-600" />
            <h2 className="mt-3 font-display text-xl font-semibold text-brand-navy">Nessun punto NFC</h2>
            <p className="mx-auto mt-1 max-w-md text-sm text-stone-500">Crea il primo punto fisico, poi associa un tag verificato prima di attivarlo.</p>
          </SettingsCard>
        )}
        {!loading && !error && points.length > 0 && (
          <>
            <div className="hidden overflow-hidden rounded-2xl border border-stone-200 bg-white md:block">
              <div className="grid grid-cols-[minmax(180px,1.2fr)_minmax(130px,0.9fr)_minmax(100px,0.7fr)_minmax(120px,0.8fr)_minmax(150px,1fr)_auto] gap-4 bg-stone-50 px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-stone-500">
                <span>Punto</span><span>Azienda / locale</span><span>Reparto</span><span>Tipo</span><span>Stato / tag</span><span>Azioni</span>
              </div>
              {points.map((point) => renderPoint(point))}
            </div>
            <div className="space-y-3 md:hidden">{points.map((point) => renderPoint(point, true))}</div>
          </>
        )}
      </div>

      <Dialog open={Boolean(editor)} onOpenChange={(open) => !open && !saving && setEditor(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editor?.mode === 'create' ? 'Nuovo punto NFC' : 'Modifica punto NFC'}</DialogTitle>
            <DialogDescription>Il punto rappresenta un luogo fisico e non una persona.</DialogDescription>
          </DialogHeader>
          {editor && (
            <div className="grid gap-4 py-2 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="nfc-company">Azienda</Label>
                <select id="nfc-company" value={editor.form.company_id} onChange={(event) => updateEditor('company_id', event.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                  <option value="">Seleziona azienda</option>
                  {companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="nfc-location">Locale</Label>
                <select id="nfc-location" value={editor.form.location_id} onChange={(event) => updateEditor('location_id', event.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                  <option value="">Seleziona locale</option>
                  {availableLocations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="nfc-department">Reparto <span className="font-normal text-stone-400">(facoltativo)</span></Label>
                <select id="nfc-department" value={editor.form.department_id} onChange={(event) => updateEditor('department_id', event.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                  <option value="">Tutti i reparti</option>
                  {availableDepartments.map((department) => <option key={department.id} value={department.id}>{department.name}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="nfc-type">Tipo punto</Label>
                <select id="nfc-type" value={editor.form.point_type} onChange={(event) => updateEditor('point_type', event.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                  {POINT_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="nfc-name">Nome punto</Label>
                <Input id="nfc-name" value={editor.form.name} onChange={(event) => updateEditor('name', event.target.value)} placeholder="Entrata Cucina" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nfc-code">Codice univoco</Label>
                <Input id="nfc-code" value={editor.form.code} onChange={(event) => updateEditor('code', event.target.value)} placeholder="CUC-ENTRATA-01" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="nfc-description">Descrizione <span className="font-normal text-stone-400">(facoltativa)</span></Label>
                <Textarea id="nfc-description" value={editor.form.description} onChange={(event) => updateEditor('description', event.target.value)} placeholder="Indicazioni sul punto fisico" rows={3} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(null)} disabled={saving}>Annulla</Button>
            <Button onClick={savePoint} disabled={saving || !editor?.form.company_id || !editor?.form.location_id || !editor?.form.name.trim() || !editor?.form.code.trim()}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salva punto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(preflightPoint)}
        onOpenChange={(open) => {
          if (!open && !preflightBusy) {
            setPreflightPoint(null);
            setPreflightResult(null);
            setPreflightError('');
          }
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Preflight NFC · {preflightPoint?.name}</DialogTitle>
            <DialogDescription>
              Verifica locale del tag NTAG 424 DNA. Nessun UID, chiave, URL SUN o contenuto dei record viene restituito alla pagina.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-900">
              Questa verifica non scrive né modifica i record. Una lettura NDEF può incrementare il contatore SUN interno del tag. Nessuna autenticazione viene tentata; se una risposta non è valida, la verifica si arresta. Il writer resta disabilitato.
            </div>
            <section className="space-y-2 rounded-xl border border-stone-200 p-3" aria-label="Profilo SUN candidato">
              <h3 className="font-semibold text-stone-900">Profilo SUN candidato · non ancora autorizzato alla scrittura</h3>
              <p>AID NDEF <code>D2760000850101</code> · file <code>E104</code> (file numero <code>02</code>) · capacità <code>256 B</code>.</p>
              <p>SDM options <code>D1</code> · file access rights <code>00E0</code> · SDM access rights <code>FF21</code>.</p>
              <p>Offset nel file NDEF: PICC data <code>0x25</code>, input CMAC / dati cifrati <code>0x4A</code>, CMAC <code>0x70</code>.</p>
              <p>Slot candidati: App Master <code>0</code>, SDM file-read <code>1</code>, SDM meta-read <code>2</code>, TagTamper <code>3</code> non configurato, applicativo <code>4</code> riservato. Key version proposta <code>1</code>; non letta dal tag.</p>
              <p className="text-stone-600">I vecchi URL del prototipo vengono soltanto inventariati ora. Il futuro provisioning autorizzato li sostituirà con una sola URI SUN RestaurantOS: non saranno mantenuti nel messaggio definitivo. Il preflight non verifica chiavi né TagTamper.</p>
            </section>
            {preflightError && (
              <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-red-700">
                {preflightError}
              </div>
            )}
            {preflightResult && (
              <div className="space-y-2 rounded-xl border border-stone-200 bg-stone-50 p-3" data-testid="nfc-preflight-result">
                <p>GetVersion completo: <strong>{preflightResult.version.complete ? 'sì' : 'no'}</strong></p>
                <p>Famiglia NTAG 424: <strong>{preflightResult.version.ntag424Family ? 'riconosciuta' : 'non verificata'}</strong></p>
                <p>Record NDEF letti: <strong>{preflightResult.ndef.readable ? preflightResult.ndef.recordCount : 'non leggibili'}</strong></p>
                <p>URI legacy inventariate: <strong>{preflightResult.ndef.legacyUriRecordCount}</strong></p>
                <p>GetFileSettings: <strong>{preflightResult.fileSettings.readable ? 'leggibile' : 'non leggibile senza autenticazione'}</strong></p>
                <p>Profilo SUN: <strong>{preflightResult.profileMatches ? 'corrispondente' : 'non verificato'}</strong></p>
                <p>Stato TagTamper: <strong>non letto (richiede autenticazione)</strong></p>
                <p>Scritture APDU: <strong>disabilitate</strong></p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreflightPoint(null)} disabled={preflightBusy}>Chiudi</Button>
            <Button onClick={runPreflight} disabled={preflightBusy}>
              {preflightBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {preflightBusy ? 'Verifica in corso…' : 'Avvia preflight in sola lettura'}
            </Button>
            <Button
              variant="destructive"
              disabled
              title="Richiede verifica completa e un flusso server-side sicuro"
              data-testid="nfc-preflight-write-blocked"
            >
              Scrittura tag bloccata
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(nativeDiagnostic)} onOpenChange={(open) => !open && setNativeDiagnostic(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Lettura NFC interrotta — tag non modificato</DialogTitle>
            <DialogDescription>
              La verifica autenticata era in sola lettura. Puoi copiare il codice diagnostico e inviarlo all’amministratore.
            </DialogDescription>
          </DialogHeader>
          {nativeDiagnostic && (
            <div className="space-y-3">
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
                <p>Fase: <code>{nativeDiagnostic.phase}</code></p>
                <p>Codice: <code data-testid="nfc-diagnostic-code">{nativeDiagnostic.code}</code></p>
                <p>
                  Risposta:
                  {' '}
                  <code>
                    {nativeDiagnostic.responseLength !== undefined
                      ? `${nativeDiagnostic.responseLength} byte; SW1/SW2 ${nativeDiagnostic.sw1} / ${nativeDiagnostic.sw2}`
                      : 'non disponibile (Core NFC non ha restituito una risposta valida)'}
                  </code>
                </p>
                {nativeDiagnostic.coreNfcCode && nativeDiagnostic.coreNfcCategory && (
                  <p>
                    Core NFC: <code data-testid="nfc-core-diagnostic-code">{nativeDiagnostic.coreNfcCode}</code>
                    {' / '}
                    <code data-testid="nfc-core-diagnostic-category">{nativeDiagnostic.coreNfcCategory}</code>
                  </p>
                )}
                {nativeDiagnostic.coreNfcDomain && nativeDiagnostic.coreNfcNumericCode !== undefined && (
                  <p>
                    Dominio/codice Core NFC:
                    {' '}
                    <code data-testid="nfc-core-diagnostic-domain">
                      {nativeDiagnostic.coreNfcDomain} / {nativeDiagnostic.coreNfcNumericCode}
                    </code>
                  </p>
                )}
                <p>
                  Tempo sessione:
                  {' '}
                  <code data-testid="nfc-session-elapsed">
                    {nativeDiagnostic.sessionElapsedMs} ms
                  </code>
                </p>
                {nativeDiagnostic.exchangeElapsedMs !== undefined && (
                  <p>
                    Tempo scambio:
                    {' '}
                    <code data-testid="nfc-exchange-elapsed">
                      {nativeDiagnostic.exchangeElapsedMs} ms
                    </code>
                  </p>
                )}
                <p>Sessione: <code className="break-all">{nativeDiagnostic.sessionId}</code></p>
                <p>Correlation ID: <code className="break-all">{nativeDiagnostic.correlationId}</code></p>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  data-testid="nfc-diagnostic-copy"
                  onClick={async () => {
                    try {
                      await copyDiagnosticText(formatNativeDiagnostic(nativeDiagnostic));
                      toast.success('Diagnostica copiata');
                    } catch {
                      toast.error('Impossibile copiare la diagnostica');
                    }
                  }}
                >
                  <Copy className="mr-2 h-4 w-4" /> Copia diagnostica
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </SettingsLayout>
  );
}